import { motion } from "framer-motion";
import { ExternalLink, MessageCircle, Users } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/config";

function WhatsAppIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

export default function WhatsAppWidget() {
  const WA_URL = WHATSAPP_URL;

  return (
    <section
      data-testid="whatsapp-widget-section"
      className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 mt-16 sm:mt-24 lg:mt-32"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="glass-card ring-border p-5 sm:p-7 lg:p-8 relative overflow-hidden"
      >
        {/* Soft brand glow */}
        <div className="pointer-events-none absolute -top-24 -right-20 h-56 w-56 rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-emerald-500/10 blur-3xl" />

        <div className="relative grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6 lg:gap-8 items-center">
          {/* LEFT: branding */}
          <div className="min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              <span className="shrink-0 grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-700 ring-1 ring-white/20 shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)]">
                <WhatsAppIcon className="h-5 w-5 text-white" />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  WhatsApp Community
                </div>
                <div className="text-base sm:text-lg font-medium text-white truncate">
                  OriginPS WhatsApp
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-end gap-4">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-gradient-white">
                  ~24/7
                </span>
                <span className="text-xs sm:text-sm text-slate-400">
                  active group
                </span>
              </div>
              <span className="hidden sm:inline-flex h-2 w-2 rounded-full bg-emerald-400 pulse-dot mb-2" />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-300">
                <MessageCircle className="h-3.5 w-3.5 text-emerald-300" />
                Quick chat, no app needed
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-2">
              <a
                href={WA_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="whatsapp-widget-join-btn"
                className="glossy-gold inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <WhatsAppIcon className="h-4 w-4" />
                Join WhatsApp
              </a>
              <a
                href={WA_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="whatsapp-widget-open-btn"
                className="ghost-pill inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium whitespace-nowrap transition-all"
              >
                <ExternalLink className="h-4 w-4" />
                Open link
              </a>
            </div>
          </div>

          {/* RIGHT: quick info cards */}
          <div className="grid grid-cols-1 gap-3 sm:gap-4 min-w-0">
            <div className="glass-card ring-border p-5 sm:p-6">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-400">
                <Users className="h-3 w-3 text-emerald-300" />
                Community
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-4xl sm:text-5xl font-semibold text-gradient-white">
                  Always
                </span>
                <span className="text-xs text-slate-400">active</span>
              </div>
            </div>
            <div className="glass-card ring-border p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-400">
                <MessageCircle className="h-3 w-3 text-emerald-300" />
                Platform
              </div>
              <div className="mt-1 flex items-center justify-between gap-2 min-w-0">
                <span className="text-sm font-medium text-white truncate min-w-0">
                  WhatsApp Group
                </span>
                <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/20 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
                  Live
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
